// Full-page review of a registered flow at /flows/:id.
//
// Layout:
//   Header (title, status, back link)
//   Identity block (collapsible, default expanded — flow context that
//                   stays useful regardless of which node is in focus)
//   Two-column main:
//     Left (flex 1)  — ReactFlow graph, fills the remaining viewport height
//     Right (sticky) — Inspector panel:
//                        • no node selected → flow-level info (tools,
//                          decision logic, guardrails)
//                        • node selected    → that node's details with a
//                          back-to-overview link
//
// The inspector pattern is forward-compatible with a future "build" mode:
// node editing slots into the same right-hand surface.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

import { AppShell } from "./AppShell";
import { FlowGraph } from "./components/FlowGraph";
import { apiFetch } from "./lib/api";

type FlowStatus = "draft" | "review" | "active" | "deprecated";

type FlowNode = {
  id: string;
  goal: string;
  pre_conditions: string;
  mini_prompt: string;
  tools_allowed: string[];
  slots_filled: string[];
  max_turns: number;
  fallback: string;
  exits: Array<[string, string]>;
};

type FlowSummary = {
  id: string;
  name: string;
  version: string;
  status: FlowStatus;
  applies_to: string[];
  when_it_runs: string;
  out_of_scope: string;
  success_metric: string;
  business_owner: string | null;
  engineering_owner: string | null;
  agent_name: string;
  tone: string;
  greeting: string;
  closing: string;
  tools: { name: string; when: string; inputs: string; outputs: string; side_effect: string }[];
  nodes: FlowNode[];
  entry_node: string;
  decision_logic: string;
  guardrails: string;
};

type FlowsResponse = {
  source?: string;
  options?: FlowSummary[];
  error?: string;
  detail?: string;
};

export function FlowReview({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const [flows, setFlows] = useState<FlowSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [identityOpen, setIdentityOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<FlowsResponse>("/api/settings/flows");
        if (cancelled) return;
        if (data.error) setErr(data.detail ?? data.error);
        setFlows(data.options ?? []);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const flow = useMemo(() => flows?.find((f) => f.id === id) ?? null, [flows, id]);
  const selectedNode = useMemo(
    () => (flow && selectedNodeId ? flow.nodes.find((n) => n.id === selectedNodeId) ?? null : null),
    [flow, selectedNodeId],
  );

  return (
    <AppShell>
      <div id={`flow-${id}-root`} className="flow-review">
        <header id={`flow-${id}-header`} className="flow-review-header">
          <button
            id={`flow-${id}-back`}
            type="button"
            className="flow-review-back"
            onClick={() => setLocation("/settings/flows")}
          >
            ← Flows
          </button>
          {flow && (
            <div className="flow-review-title">
              <h1>{flow.name}</h1>
              <div className="flow-review-meta">
                <code>{flow.id}</code>
                <span>v{flow.version}</span>
                <span className={`flow-status flow-status-${flow.status}`}>{flow.status}</span>
                <span className="muted">applies to {flow.applies_to.join(", ")}</span>
              </div>
            </div>
          )}
        </header>

        {!flows && !err && <div id={`flow-${id}-loading`} className="muted">Loading…</div>}
        {err && <div id={`flow-${id}-error`} className="dial-err">{err}</div>}
        {flows && !flow && (
          <div id={`flow-${id}-missing`} className="flow-review-missing">
            No flow registered with id <code>{id}</code>.{" "}
            <Link id={`flow-${id}-missing-back-link`} href="/settings/flows">Back to flows</Link>
          </div>
        )}

        {flow && (
          <div id={`flow-${id}-split`} className="flow-review-split">
            <div id={`flow-${id}-graph-pane`} className="flow-review-graph-pane">
              <FlowGraph
                spec={flow}
                selectedId={selectedNodeId}
                onSelectNode={setSelectedNodeId}
              />
            </div>

            <aside id={`flow-${id}-inspector`} className="flow-review-inspector">
              {/* Identity sits at the top of the inspector as a collapsible
                  card — visible regardless of node selection, but doesn't
                  eat vertical space from the graph the way the old
                  full-width section above the split did. */}
              <IdentitySection
                flow={flow}
                open={identityOpen}
                onToggle={() => setIdentityOpen((v) => !v)}
              />

              {selectedNode ? (
                <NodeInspector
                  node={selectedNode}
                  isEntry={flow.entry_node === selectedNode.id}
                  onBack={() => setSelectedNodeId(null)}
                />
              ) : (
                <FlowOverviewInspector flow={flow} />
              )}
            </aside>
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Identity — collapsible, default open. Stays visible even when a node is
// being inspected so the operator can cross-reference greeting/persona.
// ---------------------------------------------------------------------------
function IdentitySection({
  flow,
  open,
  onToggle,
}: {
  flow: FlowSummary;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <section id={`flow-${flow.id}-identity`} className={`flow-review-identity ${open ? "is-open" : ""}`}>
      <button id={`flow-${flow.id}-identity-toggle`} type="button" className="flow-review-identity-toggle" onClick={onToggle}>
        <span className="flow-review-identity-caret" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        Identity
      </button>
      {open && (
        <dl id={`flow-${flow.id}-identity-dl`} className="flow-review-dl">
          <dt>When it runs</dt><dd>{flow.when_it_runs}</dd>
          <dt>Out of scope</dt><dd>{flow.out_of_scope}</dd>
          <dt>Success metric</dt><dd>{flow.success_metric}</dd>
          <dt>Persona</dt><dd>{flow.agent_name} — {flow.tone}</dd>
          <dt>Greeting</dt><dd className="flow-quote">{flow.greeting}</dd>
          <dt>Closing</dt><dd className="flow-quote">{flow.closing}</dd>
          <dt>Owners</dt>
          <dd>
            business: {flow.business_owner ?? "TBD"} · eng: {flow.engineering_owner ?? "TBD"}
          </dd>
        </dl>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inspector — flow-level (default state, no node selected).
// ---------------------------------------------------------------------------
function FlowOverviewInspector({ flow }: { flow: FlowSummary }) {
  return (
    <div id={`flow-${flow.id}-inspector-overview`} className="inspector">
      <header className="inspector-head">
        <div className="inspector-eyebrow">Flow overview</div>
        <div className="inspector-hint">Click a node in the graph to inspect it.</div>
      </header>

      {flow.decision_logic && (
        <Section title="Decision logic">
          <p className="flow-prose">{flow.decision_logic}</p>
        </Section>
      )}
      {flow.guardrails && (
        <Section title="Guardrails">
          <p className="flow-prose">{flow.guardrails}</p>
        </Section>
      )}
      <Section title={`Tools (${flow.tools.length})`}>
        <ul id={`flow-${flow.id}-inspector-tools`} className="inspector-tools">
          {flow.tools.map((t) => (
            <li id={`flow-${flow.id}-inspector-tool-${t.name}`} key={t.name}>
              <code>{t.name}</code>
              <span className="muted"> — {t.when}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inspector — selected node.
// ---------------------------------------------------------------------------
function NodeInspector({
  node,
  isEntry,
  onBack,
}: {
  node: FlowNode;
  isEntry: boolean;
  onBack: () => void;
}) {
  return (
    <div id={`flow-review-inspector-node-${node.id}`} className="inspector">
      <header className="inspector-head">
        <button id={`flow-review-inspector-back-${node.id}`} type="button" className="inspector-back" onClick={onBack}>
          ← Flow overview
        </button>
        <div className="inspector-title">
          <code>{node.id}</code>
          {isEntry && <span className="flow-entry-tag">entry</span>}
          <span className="inspector-turns">max {node.max_turns} turns</span>
        </div>
      </header>

      <p className="inspector-goal">{node.goal}</p>

      <Section title="Mini-prompt">
        <p className="flow-prose">{node.mini_prompt}</p>
      </Section>

      <Section title="Pre-conditions">
        <p className="flow-prose">{node.pre_conditions || "—"}</p>
      </Section>

      <Section title="Tools allowed">
        {node.tools_allowed.length === 0 ? (
          <p className="muted">—</p>
        ) : (
          <div className="inspector-pills">
            {node.tools_allowed.map((t) => (
              <code key={t} className="flow-pill">{t}</code>
            ))}
          </div>
        )}
      </Section>

      <Section title="Slots filled">
        {node.slots_filled.length === 0 ? (
          <p className="muted">—</p>
        ) : (
          <div className="inspector-pills">
            {node.slots_filled.map((s) => (
              <code key={s} className="flow-pill">{s}</code>
            ))}
          </div>
        )}
      </Section>

      <Section title="Exits">
        {node.exits.length === 0 ? (
          <p className="muted">—</p>
        ) : (
          <ul className="flow-review-exits">
            {node.exits.map(([target, condition], i) => (
              <li key={`${target}-${i}`}>
                → <code>{target}</code> <span className="muted">({condition})</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Fallback">
        <p className="flow-prose">{node.fallback}</p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="inspector-section">
      <h3 className="inspector-section-title">{title}</h3>
      {children}
    </section>
  );
}
